import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import { Button, PageTitle } from "../StyledElements";
import { Dialog } from "../Dialog";
import { SchemaEditor } from "../Metadata/SchemaEditor";
import { JsonEditor } from "../Metadata/JsonEditor";
import {
  createDatasetType,
  deactivateDatasetType,
  updateDatasetType,
} from "../../services/datasetTypes";
import { useDatasetTypes } from "../../hooks/useDatasetTypes";
import { cascadeTypeSchema } from "../../services/datasetTypeCascade";
import { normalizeMetadataErrors } from "../../utils/datasetMetadata";

// Admin registry for dataset types: a type bundles a label, an item metadata schema and an
// optional ingestion workflow. Datasets created from a type copy those values.

const NAME_PATTERN = /^[a-z0-9][a-z0-9_-]{0,99}$/;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  color: var(--main-font-color);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const Card = styled.div`
  border: 1px solid var(--content-item-border-color);
  background-color: var(--card-bg);
  border-radius: 8px;
  padding: 16px;
  box-shadow: var(--content-item-box-shadow);
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr 1.6fr 0.8fr 0.7fr auto;
  gap: 10px;
  align-items: center;
  padding: 10px 8px;
  border-bottom: 1px solid var(--content-item-border-color);
  font-size: 13px;
  color: var(--main-font-color);

  &:last-child {
    border-bottom: none;
  }
`;

const HeadRow = styled(Row)`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #a0a6b1;
`;

const Muted = styled.div`
  font-size: 13px;
  font-style: italic;
  color: #a0a6b1;
`;

const Badge = styled.span`
  display: inline-flex;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${(props) => (props.$active ? "#2ecc71" : "#95a5a6")};
  border: 1px solid ${(props) => (props.$active ? "#2ecc71" : "#95a5a6")};
  background-color: ${(props) => (props.$active ? "rgba(46, 204, 113, 0.12)" : "rgba(149, 165, 166, 0.12)")};
`;

const Mono = styled.span`
  font-family: monospace;
  font-size: 12px;
`;

const Small = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  font-size: 12px;
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  background: transparent;
  color: ${(props) => (props.$danger ? "#e74c3c" : "var(--main-font-color)")};
  cursor: pointer;
`;

const Actions = styled.div`
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--main-font-color);
`;

const inputStyles = `
  width: 100%;
  padding: 8px 10px;
  font-size: 14px;
  font-family: inherit;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  outline: none;
  box-sizing: border-box;
`;

const Input = styled.input`
  ${inputStyles}
`;

const Textarea = styled.textarea`
  ${inputStyles}
  resize: vertical;
`;

const Hint = styled.div`
  font-size: 12px;
  font-style: italic;
  color: #a0a6b1;
`;

const ErrorBanner = styled.div`
  padding: 9px 11px;
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid #e74c3c;
  background-color: rgba(231, 76, 60, 0.1);
  color: #e74c3c;
`;

const emptyForm = {
  name: "",
  label: "",
  description: "",
  metadata_schema: null,
  ingestion_workflow: null,
  is_active: true,
};

const fieldCount = (schema) => {
  if (!schema || typeof schema !== "object") return null;
  const properties = schema.properties && typeof schema.properties === "object" ? Object.keys(schema.properties) : [];
  return properties.length || null;
};

export const DatasetTypesManager = () => {
  const { types, loading, error, reload } = useDatasetTypes({ includeInactive: true });

  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [actionError, setActionError] = useState(null);

  const [cascade, setCascade] = useState(true);
  const [cascadeSummary, setCascadeSummary] = useState(null);
  const [cascading, setCascading] = useState(false);

  useEffect(() => {
    if (!showDialog) return;
    setForm(editing ? { ...emptyForm, ...editing } : emptyForm);
    setFormError(null);
    setFieldErrors([]);
    setSaving(false);
    setCascade(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDialog, editing]);

  const openCreate = () => {
    setEditing(null);
    setShowDialog(true);
  };

  const openEdit = (type) => {
    setEditing(type);
    setShowDialog(true);
  };

  const submit = async () => {
    if (saving) return;

    const name = String(form.name ?? "").trim();
    const label = String(form.label ?? "").trim();

    if (!editing && !NAME_PATTERN.test(name)) {
      setFormError("Name must be lowercase letters, digits, hyphens or underscores (max 100).");
      return;
    }

    if (!form.metadata_schema || typeof form.metadata_schema !== "object") {
      setFormError("A metadata schema (JSON object) is required.");
      return;
    }

    setSaving(true);
    setFormError(null);
    setFieldErrors([]);

    const payload = editing
      ? {
          label: label || editing.label,
          description: form.description ?? "",
          metadata_schema: form.metadata_schema,
          ingestion_workflow: form.ingestion_workflow ?? null,
          is_active: form.is_active !== false,
        }
      : {
          name,
          label: label || name,
          description: form.description ?? "",
          metadata_schema: form.metadata_schema,
          ingestion_workflow: form.ingestion_workflow ?? null,
        };

    const result = editing
      ? await updateDatasetType(editing.public_id, payload)
      : await createDatasetType(payload);

    setSaving(false);

    if (result?.success === false) {
      if (result?.status === 409) {
        setFormError("A dataset type with that name already exists.");
      } else {
        setFormError(result.message || "Could not save the dataset type.");
      }
      setFieldErrors(normalizeMetadataErrors(result));
      return;
    }

    setShowDialog(false);
    reload();

    // Editing a type doesn't rewrite datasets server-side: push the new schema's missing fields
    // onto every dataset bound to the type.
    if (editing && cascade) {
      setCascading(true);

      try {
        const summary = await cascadeTypeSchema(
          { ...editing, ...payload, public_id: editing.public_id },
          payload.metadata_schema
        );
        setCascadeSummary(summary);
      } catch (failure) {
        setCascadeSummary({ total: 0, updated: 0, unchanged: 0, failed: [{ name: "—", message: failure?.message }] });
      }

      setCascading(false);
    }
  };

  const deactivate = async (type) => {
    setActionError(null);
    setConfirmDeactivate(null);

    const result = await deactivateDatasetType(type.public_id);

    if (result?.success === false) {
      setActionError(result.message || "Could not deactivate the dataset type.");
      return;
    }

    reload();
  };

  const rows = useMemo(() => types ?? [], [types]);

  return (
    <Wrapper>
      <Header>
        <PageTitle title="Dataset Types" style={{ marginBottom: 0 }} />
        <span style={{ flex: 1 }} />
        <Small onClick={reload} title="Reload">
          <Icon icon="lucide:refresh" /> Reload
        </Small>
        <Button onClick={openCreate}>+ New type</Button>
      </Header>

      <Muted>
        A type bundles an item metadata schema (and an optional ingestion workflow). Datasets
        created from a type copy those values and keep them if the type changes later.
      </Muted>

      {error ? <ErrorBanner>{error}</ErrorBanner> : null}
      {actionError ? <ErrorBanner>{actionError}</ErrorBanner> : null}

      {cascading ? <Muted>Updating the datasets that use this type…</Muted> : null}

      {cascadeSummary ? (
        <div
          style={{
            padding: "10px 12px",
            fontSize: "13px",
            borderRadius: "6px",
            border: `1px solid ${cascadeSummary.failed.length ? "#f39c12" : "#2ecc71"}`,
            backgroundColor: cascadeSummary.failed.length ? "rgba(243, 156, 18, 0.1)" : "rgba(46, 204, 113, 0.1)",
            color: cascadeSummary.failed.length ? "#f39c12" : "#2ecc71",
          }}
        >
          <div>
            Dataset schemas: {cascadeSummary.updated} updated, {cascadeSummary.unchanged} unchanged
            {cascadeSummary.failed.length ? `, ${cascadeSummary.failed.length} failed` : ""}
            {" "}({cascadeSummary.total} dataset{cascadeSummary.total === 1 ? "" : "s"} using this type).
          </div>
          {cascadeSummary.failed.length > 0 ? (
            <ul style={{ margin: "6px 0 0", paddingLeft: "18px" }}>
              {cascadeSummary.failed.map((entry, index) => (
                <li key={index}>
                  {entry.name}: {entry.message}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <Card>
        {loading ? (
          <Muted>Loading types…</Muted>
        ) : rows.length === 0 ? (
          <Muted>No dataset types yet.</Muted>
        ) : (
          <>
            <HeadRow>
              <span>Label</span>
              <span>Name</span>
              <span>Description</span>
              <span>Fields</span>
              <span>Status</span>
              <span />
            </HeadRow>

            {rows.map((type) => {
              const count = fieldCount(type.metadata_schema);
              const active = type.is_active !== false;

              return (
                <Row key={type.public_id ?? type.name}>
                  <span>{type.label ?? type.name}</span>
                  <Mono>{type.name}</Mono>
                  <span style={{ color: "#a0a6b1" }}>{type.description || "—"}</span>
                  <span>{count ?? "—"}</span>
                  <Badge $active={active}>{active ? "Active" : "Inactive"}</Badge>
                  <Actions>
                    <Small onClick={() => openEdit(type)}>
                      <Icon icon="lucide:pencil" /> Edit
                    </Small>
                    {confirmDeactivate === type.public_id ? (
                      <>
                        <Small $danger onClick={() => deactivate(type)}>
                          <Icon icon="lucide:check" /> Confirm
                        </Small>
                        <Small onClick={() => setConfirmDeactivate(null)}>
                          <Icon icon="lucide:x" /> Cancel
                        </Small>
                      </>
                    ) : (
                      <Small
                        $danger
                        onClick={() => setConfirmDeactivate(type.public_id)}
                        disabled={!active}
                        title={active ? "Deactivate this type" : "Already inactive"}
                      >
                        <Icon icon="lucide:ban" /> Deactivate
                      </Small>
                    )}
                  </Actions>
                </Row>
              );
            })}
          </>
        )}
      </Card>

      <Dialog
        title={editing ? "Edit dataset type" : "New dataset type"}
        subtitle="Changing a type does not rewrite existing datasets"
        show={showDialog}
        onClose={() => setShowDialog(false)}
        onOK={submit}
        okText={saving ? "Saving…" : "Save"}
        closeText="Cancel"
        style={{ width: "90%", maxWidth: "900px", height: "85vh" }}
      >
        {editing ? null : (
          <Field>
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              placeholder="vault"
              autoFocus
            />
            <Hint>Lowercase slug — datasets store this in their `type` field.</Hint>
          </Field>
        )}

        <Field>
          <Label>Label</Label>
          <Input
            value={form.label}
            onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))}
            placeholder="Vault"
          />
        </Field>

        <Field>
          <Label>Description</Label>
          <Textarea
            rows={2}
            value={form.description}
            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
            placeholder="Long-form knowledge items"
          />
        </Field>

        <SchemaEditor
          hideLabel
          value={form.metadata_schema}
          onChange={(schema) => setForm((current) => ({ ...current, metadata_schema: schema }))}
        />

        <JsonEditor
          label="Default ingestion workflow (optional)"
          hint="Leave empty to use the standard ingestion graph. Must be an ingestion graph ending in chromaInputNode."
          value={form.ingestion_workflow}
          onChange={(workflow) => setForm((current) => ({ ...current, ingestion_workflow: workflow }))}
          requiredObject
          minHeight="140px"
        />

        {editing ? (
          <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
            <input
              type="checkbox"
              checked={form.is_active !== false}
              onChange={(event) => setForm((current) => ({ ...current, is_active: event.target.checked }))}
            />
            Active
          </label>
        ) : null}

        {editing ? (
          <label style={{ display: "inline-flex", alignItems: "flex-start", gap: "6px", fontSize: "13px" }}>
            <input
              type="checkbox"
              checked={cascade}
              onChange={(event) => setCascade(event.target.checked)}
              style={{ marginTop: "2px" }}
            />
            <span>
              Add this schema's missing fields to the datasets using this type
              <span style={{ display: "block", fontSize: "12px", color: "#a0a6b1" }}>
                Datasets keep their own fields and customisations; only the type's new fields are added.
              </span>
            </span>
          </label>
        ) : null}

        {formError ? <ErrorBanner>{formError}</ErrorBanner> : null}

        {fieldErrors.length > 0 ? (
          <ErrorBanner>
            {fieldErrors.map((entry, index) => (
              <div key={`${entry.path}-${index}`}>
                <code>{entry.path}</code>: {entry.message}
              </div>
            ))}
          </ErrorBanner>
        ) : null}
      </Dialog>
    </Wrapper>
  );
};

export default DatasetTypesManager;
